import React, { useState, useEffect, useRef } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Switch,
  FormControlLabel,
  Autocomplete,
  IconButton,
  Tooltip,
  InputAdornment,
  Typography,
} from '@mui/material';
import { IconChevronDown, IconTrash, IconRefresh, IconInfoCircle, IconCloudCheck } from '@tabler/icons-react';
import type { CatalogProduct, ResolvedPricingProfile, ResolvedRateSlot } from '@techgitter/contracts';
import { OverrideState } from '../../types/ticketTypeTemplate';
import { resetFieldOverride } from '../../utils/templateEngine';
import { SearchAhead } from '../dropdown';
import './LineItemCard.css';

export interface LineItem {
  id: string;
  source: 'manual' | 'ticketTypeTemplate' | 'rs';
  templateKey?: string;
  rsLineItemId?: number;
  converted?: boolean;
  lineItemId: string;
  category: string;
  quantity: number;
  unitCost: number;
  prefixUserBase: string;
  afterHours: boolean;
  nonChargeable: boolean;
  lineDetail: string;
  productDescription?: string;
  productId?: string;
  subtotal: number;
  finalTotal: number;
  overrideState?: OverrideState;
  pricingExplanation?: string;
  pending?: boolean;
}

export type BillingStatus = 'unbilled' | 'invoiced' | 'paid';

interface LineItemCardProps {
  index: number;
  lineItem: LineItem;
  expanded: boolean;
  canDelete?: boolean;
  invoicePaid?: boolean;
  invoiceId?: number;
  onExpandChange: (id: string, expanded: boolean) => void;
  onUpdate: (id: string, updates: Partial<LineItem>) => void;
  onDelete: (id: string) => void;
  /** Provision a license key for this line (license-key SKUs only). Allocates a
   *  key to the ticket's customer and writes it into the line detail. When
   *  omitted (e.g. ticket has no customer), the Provision button is hidden. */
  onProvisionLicense?: (id: string) => Promise<void>;
  products?: CatalogProduct[];
  resolvedPricingProfile?: ResolvedPricingProfile;
  afterHoursContext?: {
    isAfterHours: boolean;
    window: string;
    reason?: string;
  };
  autoFocus?: boolean;
  quantityError?: boolean;
  /** 'v2' restyles the collapsed row to the technician-card panel aesthetic
   *  (hairline border, 6px radius, dashboard-tier type, smaller delete button).
   *  Defaults to 'legacy' so existing usages are unchanged. */
  variant?: 'legacy' | 'v2';
}

const BILLING_CHIP_CONFIG: Record<BillingStatus, { label: string; color: 'default' | 'info' | 'success' }> = {
  unbilled: { label: 'Unbilled', color: 'default' },
  invoiced: { label: 'Invoiced', color: 'info' },
  paid: { label: 'Paid', color: 'success' },
};

export const LineItemCard: React.FC<LineItemCardProps> = ({
  index,
  lineItem,
  expanded,
  canDelete = true,
  invoicePaid,
  invoiceId,
  onExpandChange,
  onUpdate,
  onDelete,
  onProvisionLicense,
  products,
  resolvedPricingProfile,
  afterHoursContext,
  autoFocus,
  quantityError,
  variant = 'legacy',
}) => {
  const v2 = variant === 'v2';
  const quantityRef = useRef<HTMLInputElement>(null);

  // License provisioning: this line's product is a resale-license SKU, so a
  // "Provision License" action lets a tech allocate a key against the ticket.
  const isLicenseLine = React.useMemo(
    () => !!products?.some((p) => p.sku === lineItem.lineItemId && p.isLicenseKey),
    [products, lineItem.lineItemId],
  );
  const licenseProvisioned = /license key:/i.test(lineItem.lineDetail || '');
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState<string | null>(null);

  const handleProvision = async () => {
    if (!onProvisionLicense) return;
    setProvisioning(true);
    setProvisionError(null);
    try {
      await onProvisionLicense(lineItem.id);
      setProvisionOpen(false);
    } catch (e) {
      setProvisionError(e instanceof Error ? e.message : 'Failed to provision license');
    } finally {
      setProvisioning(false);
    }
  };

  // When quantityError becomes true, expand the card, focus the field, and flash it
  useEffect(() => {
    if (!quantityError) return;
    onExpandChange(lineItem.id, true);
    // Delay focus slightly to allow accordion expansion animation
    const timer = setTimeout(() => {
      quantityRef.current?.focus();
      quantityRef.current?.select();
    }, 150);
    return () => clearTimeout(timer);
  }, [quantityError, lineItem.id, onExpandChange]);
  const productOptions = React.useMemo(() => {
    if (!products || products.length === 0) return [];
    // Dedupe by SKU — the catalog can contain multiple products sharing a SKU,
    // and duplicate option strings confuse MUI Autocomplete's filtering.
    const bySku = new Map<string, CatalogProduct>();
    for (const p of products) {
      if (!p.disabled) bySku.set(p.sku, p);
    }
    return [...bySku.values()].sort((a, b) => a.sku.localeCompare(b.sku));
  }, [products]);

  const productBySku = React.useMemo(() => {
    const map = new Map<string, CatalogProduct>();
    productOptions.forEach((p) => map.set(p.sku, p));
    return map;
  }, [productOptions]);

  // Reverse lookup keyed by (normalised) display name. The v2 picker shows the
  // product's description as the field value and commits free text on blur, so
  // a blurred description can arrive at `handleLineItemChange` instead of a SKU
  // (e.g. when `selectedProduct` momentarily can't resolve and `productDisplay`
  // falls back to the SKU, tripping the blur guard). Resolving that description
  // back to its product keeps the long description from becoming the code.
  const normalizeDisplay = React.useCallback(
    // Whitespace-INSENSITIVE on purpose. A single-line <input> silently strips
    // the newlines out of a product's multi-line display name, so the blurred
    // field text has NO whitespace where the catalog value has "\n". Collapsing
    // runs of whitespace to a single space still left the two unequal at
    // "…license)\n•…" boundaries (no space adjacent to the newline). Removing all
    // whitespace makes the stripped input text match the catalog display name so
    // a blurred description resolves back to its product instead of the code.
    (s: string) => s.replace(/\s+/g, '').toLowerCase(),
    [],
  );
  const productByDisplay = React.useMemo(() => {
    const map = new Map<string, CatalogProduct>();
    for (const p of productOptions) {
      const key = normalizeDisplay(p.displayName ?? '');
      if (key && !map.has(key)) map.set(key, p);
    }
    return map;
  }, [productOptions, normalizeDisplay]);

  // Product / Service search-ahead (v2). The field shows the product
  // description (display name) with the short code beneath; the committed value
  // stays the SKU. `productQuery` mirrors the displayed description and resyncs
  // when the committed line changes externally (e.g. after-hours swap).
  const selectedProduct = productBySku.get(lineItem.lineItemId);
  const productDisplay = selectedProduct?.displayName || lineItem.lineItemId || lineItem.category;
  const [productQuery, setProductQuery] = useState(productDisplay);
  useEffect(() => {
    setProductQuery(productDisplay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItem.lineItemId, lineItem.category, selectedProduct?.displayName]);

  const productResults = React.useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    const base = q
      ? productOptions.filter(
          (p) =>
            p.sku.toLowerCase().includes(q) ||
            (p.displayName?.toLowerCase().includes(q) ?? false),
        )
      : productOptions;
    return base.map((p) => ({ code: p.sku, description: p.displayName ?? '' }));
  }, [productOptions, productQuery]);

  // Bidirectional lookup: given any SKU, find its standard ↔ after-hours counterpart
  const skuCounterpartMap = React.useMemo(() => {
    const map = new Map<string, string>();
    if (!resolvedPricingProfile) return map;
    for (const slot of Object.values(resolvedPricingProfile)) {
      if (slot.sku && slot.afterHoursSku) {
        map.set(slot.sku, slot.afterHoursSku);
        map.set(slot.afterHoursSku, slot.sku);
      }
    }
    return map;
  }, [resolvedPricingProfile]);

  // Set of all after-hours SKUs from the pricing profile
  const afterHoursSkus = React.useMemo(() => {
    const set = new Set<string>();
    if (!resolvedPricingProfile) return set;
    for (const slot of Object.values(resolvedPricingProfile)) {
      if (slot.afterHoursSku) set.add(slot.afterHoursSku);
    }
    return set;
  }, [resolvedPricingProfile]);

  // Reverse lookup: SKU (standard or after-hours) → its ResolvedRateSlot
  const skuToSlot = React.useMemo(() => {
    const map = new Map<string, ResolvedRateSlot>();
    if (!resolvedPricingProfile) return map;
    for (const slot of Object.values(resolvedPricingProfile)) {
      if (slot.sku) map.set(slot.sku, slot);
      if (slot.afterHoursSku) map.set(slot.afterHoursSku, slot);
    }
    return map;
  }, [resolvedPricingProfile]);

  const [isEditingPrefix, setIsEditingPrefix] = useState(false);
  const [prefixValue, setPrefixValue] = useState(lineItem.prefixUserBase);

  // Local string state for numeric fields so intermediate values like ".25" work
  const [quantityStr, setQuantityStr] = useState(lineItem.quantity ? String(lineItem.quantity) : '');
  const [unitCostStr, setUnitCostStr] = useState(lineItem.unitCost ? String(lineItem.unitCost) : '');
  const [isEditingQuantity, setIsEditingQuantity] = useState(false);
  const [isEditingUnitCost, setIsEditingUnitCost] = useState(false);

  // Sync local string state when the model value changes externally (e.g. template sync)
  useEffect(() => {
    if (!isEditingQuantity) {
      setQuantityStr(lineItem.quantity ? String(lineItem.quantity) : '');
    }
  }, [lineItem.quantity, isEditingQuantity]);

  useEffect(() => {
    if (!isEditingUnitCost) {
      setUnitCostStr(lineItem.unitCost ? String(lineItem.unitCost) : '');
    }
  }, [lineItem.unitCost, isEditingUnitCost]);

  useEffect(() => {
    const qty = isFinite(lineItem.quantity) ? lineItem.quantity : 0;
    const cost = isFinite(lineItem.unitCost) ? lineItem.unitCost : 0;
    const subtotal = qty * cost;
    const finalTotal = lineItem.nonChargeable ? 0 : subtotal;

    if (lineItem.subtotal !== subtotal || lineItem.finalTotal !== finalTotal) {
      onUpdate(lineItem.id, { subtotal, finalTotal });
    }
  }, [lineItem.quantity, lineItem.unitCost, lineItem.nonChargeable, lineItem.subtotal, lineItem.finalTotal, lineItem.id, onUpdate]);

  const getDisplayPrefix = () => {
    if (isEditingPrefix) {
      return prefixValue;
    }
    if (isAfterHours) {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      return `${lineItem.prefixUserBase}\nAfter Hours (${hh}:${mm})`;
    }
    return lineItem.prefixUserBase;
  };

  const handlePrefixFocus = () => {
    setIsEditingPrefix(true);
    setPrefixValue(lineItem.prefixUserBase);
  };

  const handlePrefixBlur = () => {
    setIsEditingPrefix(false);
    onUpdate(lineItem.id, { prefixUserBase: prefixValue });
  };

  const handlePrefixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrefixValue(e.target.value);
  };

  const handleResetPrefix = () => {
    const defaultPrefix = lineItem.category || '';
    setPrefixValue(defaultPrefix);
    onUpdate(lineItem.id, { prefixUserBase: defaultPrefix });
  };

  const handleResetField = (field: keyof OverrideState) => {
    if (lineItem.source !== 'ticketTypeTemplate' || !lineItem.overrideState) {
      return;
    }

    const updatedItem = resetFieldOverride(lineItem, field);
    onUpdate(lineItem.id, updatedItem);
  };

  const isFieldOverridden = (field: keyof OverrideState): boolean => {
    return (
      lineItem.source === 'ticketTypeTemplate' &&
      lineItem.overrideState?.[field] === true
    );
  };

  const handleLineItemChange = (_: any, value: string | null) => {
    const newValue = value || '';
    // Resolve by SKU first; fall back to matching a product's display name so a
    // description committed via the picker's free-text blur snaps back to its
    // product (and its SKU) rather than being written verbatim as the code.
    let product =
      productBySku.get(newValue) ??
      productByDisplay.get(normalizeDisplay(newValue));

    if (product) {
      // Auto-swap to after-hours variant if currently after-hours
      if (afterHoursContext?.isAfterHours && !afterHoursSkus.has(product.sku)) {
        const ahSku = skuCounterpartMap.get(product.sku);
        const ahProduct = ahSku ? productBySku.get(ahSku) : undefined;
        if (ahProduct) product = ahProduct;
      }

      const prevDescription = lineItem.productDescription ?? '';
      const lineDetailUnchanged = !lineItem.lineDetail || lineItem.lineDetail === prevDescription;

      // Resolve default quantity from pricing profile for current time window
      const slot = skuToSlot.get(product.sku);
      const windowKey = afterHoursContext?.window ?? 'business';
      const defaultQty = slot?.defaultQuantity?.[windowKey];

      onUpdate(lineItem.id, {
        category: product.category,
        lineItemId: product.sku,
        prefixUserBase: product.displayName,
        productDescription: product.description,
        productId: product.productId,
        unitCost: product.unitCost,
        afterHours: afterHoursSkus.has(product.sku),
        ...(lineDetailUnchanged && product.description ? { lineDetail: product.description } : {}),
        ...(defaultQty && defaultQty > 0 ? { quantity: defaultQty } : {}),
      });
      setPrefixValue(product.displayName);
    } else {
      // Guard against the picker's free-text blur clobbering a committed SKU
      // with the product's own description. The field DISPLAYS the description,
      // and the blur can fire while the async product catalog is momentarily
      // unresolved (e.g. an extra re-render when the ticket already has line
      // items) — so `productBySku`/`productByDisplay` above both miss and we'd
      // otherwise write the long description in as the code. This compares
      // against the line's OWN stored description, so it holds even when the
      // catalog isn't loaded: if the incoming text is just this line's product
      // description, keep the existing code instead of overwriting it.
      const committedDescription = lineItem.productDescription ?? '';
      if (
        newValue &&
        committedDescription &&
        lineItem.lineItemId &&
        normalizeDisplay(newValue) === normalizeDisplay(committedDescription)
      ) {
        return;
      }
      onUpdate(lineItem.id, {
        category: newValue,
        lineItemId: newValue,
        prefixUserBase: newValue,
      });
      setPrefixValue(newValue);
    }
  };

  const isAfterHoursSku = afterHoursSkus.has(lineItem.lineItemId);
  const hasSkuCounterpart = skuCounterpartMap.has(lineItem.lineItemId);
  const isAfterHours = hasSkuCounterpart ? isAfterHoursSku : lineItem.afterHours;
  const categoryLower = lineItem.category.toLowerCase();
  const isLabourCategory = categoryLower.includes('labour') || categoryLower.includes('support');

  const costInputSx = {
    '& .MuiInputBase-root': {
      bgcolor: '#FFFFFF',
      color: '#111827',
    },
    '& .MuiInputLabel-root': {
      color: '#6B7280',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#6B7280',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E5E7EB',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#D1D5DB',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#2563EB',
      borderWidth: 1,
    },
    '& .MuiOutlinedInput-root.Mui-focused': {
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)',
    },
    '& .MuiInputBase-input': {
      color: '#111827',
    },
  };

  // V2 panel-aligned input styling (hairline #d0dcea, #4A90D9 focus). Value
  // text is 14px from the theme.
  const v2InputSx = {
    '& .MuiInputBase-root': { bgcolor: '#FFFFFF', color: '#1a1a1a', borderRadius: '6px' },
    '& .MuiInputLabel-root': { color: '#646464' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#4A90D9' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0dcea', borderWidth: '0.5px' },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#c5d9f0', borderWidth: '0.5px' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4A90D9', borderWidth: '0.5px' },
    // Border-only focus (no glow) to match the panel's other inputs — the log
    // textarea and ProductSearchField. Suppresses the theme's accent focus ring.
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none' },
    '& .MuiInputBase-input': { color: '#1a1a1a' },
  };

  const inputSx = v2 ? v2InputSx : costInputSx;

  const handleAfterHoursChange = () => {
    const currentSku = lineItem.lineItemId;

    // Use pricing profile to find the counterpart SKU
    const counterpartSku = skuCounterpartMap.get(currentSku);
    const counterpartProduct = counterpartSku ? productBySku.get(counterpartSku) : undefined;

    if (counterpartProduct) {
      onUpdate(lineItem.id, {
        afterHours: !isAfterHoursSku,
        lineItemId: counterpartProduct.sku,
        category: counterpartProduct.category,
        unitCost: counterpartProduct.unitCost,
        prefixUserBase: counterpartProduct.displayName,
        productDescription: counterpartProduct.description,
        productId: counterpartProduct.productId,
      });
      setPrefixValue(counterpartProduct.displayName);
    } else {
      // No paired variant in pricing profile — just toggle the flag
      onUpdate(lineItem.id, { afterHours: !lineItem.afterHours });
    }
  };

  const formatCurrency = (value: number) => {
    return `$${(isFinite(value) ? value : 0).toFixed(2)}`;
  };

  const getHeaderText = () => {
    const name = lineItem.lineItemId || lineItem.category || `Line Item ${index + 1}`;
    if (!lineItem.unitCost && !lineItem.category) return name;
    if (lineItem.quantity === 1) return `${name} — ${formatCurrency(lineItem.unitCost)}`;
    return `${name} — ${lineItem.quantity}x at ${formatCurrency(lineItem.unitCost)}ea`;
  };

  const getSubheaderText = () => {
    const parts = [];
    if (lineItem.prefixUserBase) parts.push(lineItem.prefixUserBase);
    if (lineItem.category) parts.push(lineItem.category);
    if (lineItem.lineDetail) parts.push(lineItem.lineDetail);
    return parts.join(' - ') || '';
  };

  const headerText = getHeaderText();
  const subheaderText = getSubheaderText();

  const billingStatus: BillingStatus | null = React.useMemo(() => {
    if (lineItem.source !== 'rs') return null;
    if (lineItem.converted && invoicePaid) return 'paid';
    if (lineItem.converted) return 'invoiced';
    return 'unbilled';
  }, [lineItem.source, lineItem.converted, invoicePaid]);

  return (
    <>
    <Accordion
      expanded={lineItem.pending ? false : expanded}
      onChange={(_, isExpanded) => {
        if (!lineItem.pending) onExpandChange(lineItem.id, isExpanded);
      }}
      sx={{
        '&:before': { display: 'none' },
        boxShadow: v2 ? '0 0.5px 2px rgba(0, 0, 0, 0.05)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
        border: v2 ? '0.5px solid' : '1px solid',
        borderColor: isAfterHours ? (v2 ? '#FAC775' : '#FBBF24') : (v2 ? '#eef2f7' : '#E5E7EB'),
        borderWidth: v2 ? '0.5px' : 1,
        borderRadius: v2 ? '6px' : 1,
        overflow: 'hidden',
        width: '100%',
        bgcolor: '#FFFFFF',
        opacity: lineItem.pending ? 0.5 : 1,
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease, opacity 0.3s ease',
        '&.Mui-expanded': {
          margin: 0,
        },
        '&:hover': {
          borderColor: isAfterHours ? (v2 ? '#EF9F27' : '#F59E0B') : (v2 ? '#d0dcea' : '#D1D5DB'),
          cursor: lineItem.pending ? 'default' : 'pointer',
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          <IconChevronDown
            size={20}
            stroke={1.5}
            style={{
              transition: 'transform 0.3s ease',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        }
        sx={{
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            justifyContent: 'space-between',
            my: 1,
            minWidth: 0,
            // Phone: let the price/chips/delete cluster wrap below the name
            // rather than forcing horizontal overflow on a chip-heavy line.
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            rowGap: 0.5,
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            transition: 'none',
          },
        }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          flexGrow: 1,
          width: 0,
          minWidth: 0,
        }}>
          <Typography variant="body1" sx={{ fontWeight: 600, ...(v2 && { fontSize: 14 }) }}>
            {headerText}
          </Typography>
          {subheaderText && (
            <Tooltip title={subheaderText} placement="bottom-start" arrow>
              <Typography
                variant="caption"
                component="div"
                sx={{
                  color: v2 ? '#646464' : 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {subheaderText}
              </Typography>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 2 }}>
          {!lineItem.lineItemId && afterHoursContext?.isAfterHours && afterHoursContext.reason && (
            <Chip
              label={afterHoursContext.reason}
              size="small"
              sx={v2 ? {
                bgcolor: '#FAEEDA',
                color: '#633806',
                border: '0.5px solid',
                borderColor: '#FAC775',
                borderRadius: '8px',
                height: 'auto',
                fontWeight: 500,
                fontSize: '10px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                '& .MuiChip-label': { px: 1, py: '2px' },
              } : {
                bgcolor: '#FFF7ED',
                color: '#78716C',
                border: '1px solid',
                borderColor: '#E7E5E4',
                borderRadius: '999px',
                height: 26,
                fontWeight: 500,
                fontSize: '0.75rem',
                '& .MuiChip-label': { px: 1 },
              }}
            />
          )}
          {isLicenseLine && licenseProvisioned && (
            <Chip size="small" variant="outlined" label="License provisioned" color="success" />
          )}
          {isLicenseLine && !licenseProvisioned && onProvisionLicense && (
            <Button
              size="small"
              variant="outlined"
              disabled={!!lineItem.pending}
              onClick={(e) => {
                e.stopPropagation();
                setProvisionError(null);
                setProvisionOpen(true);
              }}
            >
              Provision License
            </Button>
          )}
          {lineItem.pending && (
            <Chip size="small" variant="outlined" label="Syncing..." color="warning" />
          )}
          {!lineItem.pending && lineItem.rsLineItemId != null && (
            <Tooltip title="Synced with RepairShopr" placement="top">
              <IconCloudCheck size={18} stroke={1.5} color="#888888" />
            </Tooltip>
          )}
          {billingStatus && !lineItem.pending && (
            <Chip
              size="small"
              variant="outlined"
              label={BILLING_CHIP_CONFIG[billingStatus].label}
              color={BILLING_CHIP_CONFIG[billingStatus].color}
              {...(invoiceId && billingStatus !== 'unbilled' ? {
                clickable: true,
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                  window.open(`https://techsolvers.repairshopr.com/invoices/${invoiceId}`, '_blank', 'noopener,noreferrer');
                },
              } : {})}
            />
          )}
          {lineItem.category && (
            <Typography variant="body1" sx={{ fontWeight: 600, whiteSpace: 'nowrap', ...(v2 && { fontSize: 14 }) }}>
              {formatCurrency(lineItem.finalTotal)}
            </Typography>
          )}
          <Tooltip title={canDelete && !lineItem.pending ? "Delete line item" : ""} placement="top">
            <span>
            <IconButton
              size="medium"
              disabled={!canDelete || !!lineItem.pending}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(lineItem.id);
              }}
              aria-label="Delete line item"
              sx={{
                width: v2 ? 32 : 40,
                height: v2 ? 32 : 40,
                color: 'text.secondary',
                '&:hover:not(:disabled)': {
                  color: 'error.main',
                  bgcolor: 'error.lighter',
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'error.main',
                  outlineOffset: 2,
                },
              }}
            >
              <IconTrash size={18} stroke={1.5} />
            </IconButton>
            </span>
          </Tooltip>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {v2 && (
            <div className="line-item-heading">Line Item {index + 1}</div>
          )}
          {v2 ? (
            <SearchAhead
              value={productQuery}
              results={productResults}
              secondaryText={selectedProduct ? selectedProduct.sku : undefined}
              onSearch={setProductQuery}
              onSelect={(r) => handleLineItemChange(null, r.code)}
              onBlur={(text) => {
                // Commit free-text only when the user actually changed the
                // displayed name. Compare with whitespace collapsed: the
                // single-line <input> strips the newlines out of a product's
                // display name (its description), so a byte-for-byte compare
                // would always read as "edited" and clobber the committed SKU
                // with the newline-stripped description.
                // Whitespace-insensitive compare — the single-line input strips
                // the newlines out of the product's multi-line display name, so
                // only an all-whitespace-removed compare recognises an unchanged
                // name and skips the spurious free-text commit. (See the
                // normalizeDisplay note above.)
                const normalize = (s: string) => s.replace(/\s+/g, '');
                if (normalize(text) !== normalize(productDisplay)) {
                  handleLineItemChange(null, text);
                }
              }}
              autoFocus={autoFocus}
            />
          ) : (
            <Autocomplete
              size="small"
              fullWidth
              freeSolo
              autoHighlight
              value={lineItem.lineItemId || lineItem.category}
              onChange={handleLineItemChange}
              options={productOptions.map((p) => p.sku)}
              filterOptions={(options, { inputValue }) => {
                const lower = inputValue.toLowerCase();
                return options.filter((sku) => {
                  if (sku.toLowerCase().includes(lower)) return true;
                  const product = productBySku.get(sku);
                  return product?.displayName?.toLowerCase().includes(lower) ?? false;
                });
              }}
              renderOption={(props, option) => {
                const product = productBySku.get(option);
                return (
                  <li {...props} key={option}>
                    <Box>
                      <Typography variant="body2">{option}</Typography>
                      {product?.displayName && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {product.displayName}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Product / Service"
                  placeholder="Type or select a product code or description"
                  variant="outlined"
                  autoFocus={autoFocus}
                  sx={inputSx}
                />
              )}
            />
          )}
          {isLabourCategory && (
            <TextField
              fullWidth
              label="Line Item Prefix"
              placeholder="Line item prefix - auto generated from Line Item"
              variant="outlined"
              value={getDisplayPrefix()}
              onChange={handlePrefixChange}
              onFocus={handlePrefixFocus}
              onBlur={handlePrefixBlur}
              multiline={!isEditingPrefix && isAfterHours}
              sx={inputSx}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Reset to auto-generated prefix" placement="top">
                        <IconButton
                          edge="end"
                          size="medium"
                          onClick={handleResetPrefix}
                          aria-label="Reset to auto-generated prefix"
                          sx={{
                            width: 40,
                            height: 40,
                            color: 'text.secondary',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              color: 'primary.main',
                              transform: 'scale(1.1)',
                            },
                            '&:focus-visible': {
                              outline: '2px solid',
                              outlineColor: 'primary.main',
                              outlineOffset: 2,
                            },
                          }}
                        >
                          <IconRefresh size={16} stroke={1.5} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }
              }}
            />
          )}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
            {isLabourCategory && (
              <Box sx={{
                width: '50%',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderRadius: 1,
                px: 1,
                py: 0.5,
                bgcolor: '#FFFFFF',
                border: '1px solid',
                borderColor: isAfterHours ? '#E7E5E4' : '#E5E7EB',
                boxShadow: 'none',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isAfterHours}
                      onChange={handleAfterHoursChange}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#F59E0B',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#F59E0B',
                          opacity: 0.45,
                        },
                        '& .MuiSwitch-track': {
                          backgroundColor: '#D1D5DB',
                          opacity: 1,
                        },
                        '& .MuiSwitch-switchBase.Mui-focusVisible .MuiSwitch-thumb': {
                          boxShadow: '0 0 0 6px rgba(245, 158, 11, 0.25)',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                      }}
                    >
                      After Hours
                    </Typography>
                  }
                />
                {isFieldOverridden('afterHours') && (
                  <Tooltip title="Reset to template value" placement="top">
                    <IconButton
                      size="small"
                      onClick={() => handleResetField('afterHours')}
                      aria-label="Reset after hours to template value"
                    >
                      <IconRefresh size={16} stroke={1.5} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
            <Box sx={{ width: !isLabourCategory ? '100%' : '50%', display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={lineItem.nonChargeable}
                    onChange={(e) => onUpdate(lineItem.id, { nonChargeable: e.target.checked })}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: v2 ? '#4A90D9' : '#2563EB',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: v2 ? '#4A90D9' : '#2563EB',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                    Non-Chargeable
                  </Typography>
                }
              />
              {isFieldOverridden('nonChargeable') && (
                <Tooltip title="Reset to template value" placement="top">
                  <IconButton
                    size="small"
                    onClick={() => handleResetField('nonChargeable')}
                    aria-label="Reset non-chargeable to template value"
                  >
                    <IconRefresh size={16} stroke={1.5} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Quantity"
              placeholder="0"
              variant="outlined"
              size="small"
              inputRef={quantityRef}
              error={quantityError}
              helperText={quantityError ? 'Must be at least 0.1' : undefined}
              value={isEditingQuantity ? quantityStr : (lineItem.quantity || '')}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw !== '' && !/^-?\d*\.?\d*$/.test(raw)) return;
                setQuantityStr(raw);
                setIsEditingQuantity(true);
                const num = parseFloat(raw);
                if (isFinite(num)) {
                  onUpdate(lineItem.id, { quantity: num });
                }
              }}
              onFocus={() => setIsEditingQuantity(true)}
              onBlur={() => {
                setIsEditingQuantity(false);
                const num = parseFloat(quantityStr);
                onUpdate(lineItem.id, { quantity: isFinite(num) ? num : 0 });
              }}
              sx={{
                ...inputSx,
                flex: 1,
                minWidth: 120,
                ...(quantityError && {
                  animation: 'quantityFlash 1s ease-out',
                  '@keyframes quantityFlash': {
                    '0%': { backgroundColor: 'rgba(211, 47, 47, 0.15)' },
                    '100%': { backgroundColor: 'transparent' },
                  },
                }),
              }}
              slotProps={{
                input: {
                  endAdornment: isFieldOverridden('quantity') ? (
                    <InputAdornment position="end">
                      <Tooltip title="Reset to template value" placement="top">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleResetField('quantity')}
                          aria-label="Reset quantity to template value"
                        >
                          <IconRefresh size={16} stroke={1.5} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : undefined
                }
              }}
            />
            <TextField
              label="Unit Cost"
              placeholder="0.00"
              variant="outlined"
              size="small"
              value={isEditingUnitCost ? unitCostStr : (lineItem.unitCost || '')}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw !== '' && !/^-?\d*\.?\d*$/.test(raw)) return;
                setUnitCostStr(raw);
                setIsEditingUnitCost(true);
                const num = parseFloat(raw);
                if (isFinite(num)) {
                  onUpdate(lineItem.id, { unitCost: num });
                }
              }}
              onFocus={() => setIsEditingUnitCost(true)}
              onBlur={() => {
                setIsEditingUnitCost(false);
                const num = parseFloat(unitCostStr);
                onUpdate(lineItem.id, { unitCost: isFinite(num) ? num : 0 });
              }}
              sx={{ ...inputSx, flex: 1, minWidth: 120 }}
              slotProps={{
                input: {
                  endAdornment: (
                    <>
                      {lineItem.pricingExplanation && (
                        <InputAdornment position="end">
                          <Tooltip title={lineItem.pricingExplanation} placement="top" arrow>
                            <IconInfoCircle size={16} stroke={1.5} color="#4A90D9" style={{ cursor: 'help' }} />
                          </Tooltip>
                        </InputAdornment>
                      )}
                      {isFieldOverridden('unitCost') && (
                        <InputAdornment position="end">
                          <Tooltip title="Reset to template value" placement="top">
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleResetField('unitCost')}
                              aria-label="Reset unit cost to template value"
                            >
                              <IconRefresh size={16} stroke={1.5} />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      )}
                    </>
                  )
                }
              }}
            />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Line Detail"
            placeholder="Line Detail (optional) - Add detail about this line item. This field is visible to the customer."
            variant="outlined"
            value={lineItem.lineDetail}
            onChange={(e) => onUpdate(lineItem.id, { lineDetail: e.target.value })}
            sx={inputSx}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
              input: {
                endAdornment: isFieldOverridden('lineDetail') ? (
                  <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                    <Tooltip title="Reset to template value" placement="top">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleResetField('lineDetail')}
                        aria-label="Reset line detail to template value"
                      >
                        <IconRefresh size={16} stroke={1.5} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : undefined
              }
            }}
          />
        </Box>
      </AccordionDetails>
    </Accordion>

    <Dialog
      open={provisionOpen}
      onClose={() => !provisioning && setProvisionOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Provision license</DialogTitle>
      <DialogContent>
        {provisionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {provisionError}
          </Alert>
        )}
        <Typography variant="body2">
          Warning — this action is irreversible and should only be performed if the customer has
          agreed to pay the ticketed price for the license. Proceed?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setProvisionOpen(false)} disabled={provisioning}>
          Cancel
        </Button>
        <Button variant="contained" color="warning" onClick={handleProvision} disabled={provisioning}>
          {provisioning ? 'Provisioning…' : 'Proceed'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};
